import { ticketServiceApi } from "../../../app/superAdmin/ticketServiceApi";
import { API_URL } from "../../../constants/apiUrl";
import { GetQueryArgsType } from "../../../types";
import {
  ChatListParamsType,
  ChatListResponse,
  ConversationResponse,
} from "../../../types/chat";
import {
  GetChatUserListParamsType,
  GetChatUserListResponse,
  GetCommentHistoryResponse,
  GetFilterGroupResponse,
  GetGroupAttachmentsResponse,
  GetGroupChatDetailsResponse,
  GetGroupMemberResponse,
  GetMailListParamsType,
  GetMailListResponse,
  GetNotificationCount,
  GetNotifications,
  GetReplyMailByParentMailResponse,
  GetTagsResponse,
  SingleMailResponse,
} from "../../../types/mail";
import {
  GetAllCategoryResponseType,
  GetAllTicketLogResponse,
  GetAllTicketsParamsType,
  GetAllTicketsResponseType,
  GetApprovalHubTicketsParamsType,
  GetApprovalHubTicketsResponseType,
  GetEmployeeListToAddCCParamsType,
  GetEmployeeListToAddCCResponseType,
  GetMyRaisedTicketsParamsType,
  GetMyRaisedTicketsResponseType,
  GetTicketDetailsResponseType,
  GetTicketForCCParamsType,
  GetTicketForCCResponseType,
  GetTicketIDRelatedNotificationsParamsType,
  GetTicketIDRelatedNotificationsResponseType,
  GetTicketLogDetailsParamsType,
  GetTicketLogDetailsResponse,
} from "../../../types/tickets";

export const ticketApi = ticketServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    createTicket: builder.mutation({
      query: (data) => ({
        url: API_URL.createTicket,
        method: "POST",
        body: data,
      }),
    }),
    checkKeyAvailability: builder.mutation({
      query: (data) => ({
        url: API_URL.checkTemplateKeyAvailability,
        method: "POST",
        body: data,
      }),
    }),

    updateTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        return [
          "GetAllMyTicketDetails",
          "GetRaiseTickets",
          "GetAllTickets",
          { type: "GetTicketDetails", id: result?.data?.id },
        ];
      },
    }),

    raiseTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.raiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetRaiseTickets", "GetAllTicketLogs"];
        return [];
      },
    }),

    updateRaisedTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateRaisedTicket}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetRaiseTickets",
            "GetAllTicketLogs",
            { type: "GetTicketDetails", id: result.id },
          ];
        return [];
      },
    }),

    reopenTicket: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.reopenTicket}/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: (_, __, id) => {
        return [
          "GetRaiseTickets",
          "GetAllTicketLogs",
          "GetAllMyTickets",
          "GetAllMyTicketDetails",
          "GetTicketIDRelatedNotifications",
          { type: "GetTicketDetails", id },
        ];
      },
    }),

    getAllTickets: builder.query<
      GetAllTicketsResponseType,
      GetQueryArgsType<GetAllTicketsParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllTickets,
        ...queryArgs,
      }),
      providesTags: ["GetAllTickets"],
    }),

    getAllTicketLogs: builder.query<
      GetAllTicketLogResponse,
      GetQueryArgsType<GetAllTicketsParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllTicketLogs,
        ...queryArgs,
      }),
      providesTags: ["GetAllTicketLogs"],
    }),

    getTicketDetails: builder.query<GetTicketDetailsResponseType, any>({
      query: (id) => ({
        url: `${API_URL.getTicketDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetTicketDetails", id }],
    }),

    createCategory: builder.mutation({
      query: (data) => ({
        url: API_URL.createCategory,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetAllCategories"],
    }),

    updateCategory: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateCategory}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["GetAllCategories"],
    }),

    deleteTicket: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.deleteTicket}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllTickets"],
    }),

    getAllCategories: builder.query<GetAllCategoryResponseType, void>({
      query: () => ({
        url: API_URL.getAllCategories,
      }),
      providesTags: ["GetAllCategories"],
    }),

    uploadPhoto: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadPhoto,
        method: "PATCH",
        body: data,
        headers: {
          "ngrok-skip-browser-warning": "true",
        },
        withCredentials: true,
      }),
    }),
    // ==================================================================
    myRaisedTickets: builder.query<
      GetMyRaisedTicketsResponseType,
      GetQueryArgsType<GetMyRaisedTicketsParamsType>
    >({
      query: ({ params }) => ({
        url: `${API_URL.myRaisedTickets}/${params?.id}`,
        params: {
          page: params?.page,
          limit: params?.limit,
          search: params?.search,
          priority: params?.priority,
          status: params?.status,
        },
      }),
      providesTags: ["GetAllMyTickets"],
    }),

    getTicketLogDetails: builder.query<
      GetTicketLogDetailsResponse,
      GetQueryArgsType<GetTicketLogDetailsParamsType>
    >({
      query: ({ params }) => ({
        url: `${API_URL.getTicketLogDetails}/${params?.id}`,
        params: {
          emp_id: params?.emp_id,
        },
      }),
      providesTags: ["GetAllMyTicketDetails"],
    }),

    getApprovalHubTickets: builder.query<
      GetApprovalHubTicketsResponseType,
      GetQueryArgsType<GetApprovalHubTicketsParamsType>
    >({
      query: ({ params }) => ({
        url: `${API_URL.getApprovalHubTickets}/${params?.id}`,
        params: {
          page: params?.page,
          limit: params?.limit,
          search: params?.search,
          priority: params?.priority,
          role: params?.role,
          status: params?.status,
          ticket_type: params?.ticket_type,
          severity: params?.severity,
        },
      }),
      providesTags: ["GetApprovalHubTickets"],
    }),

    approveRaiseTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.approveRaiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetApprovalHubTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    rejectRaiseTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.rejectRaiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetApprovalHubTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    shiftPriority: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.shiftPriority}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllTickets",
        "GetAllTicketLogs",
        "GetAllMyTickets",
        "GetApprovalHubTickets",
        "GetTicketForCC",
      ],
    }),

    getTicketLogsDetails: builder.query({
      query: (id) => ({
        url: `${API_URL.getTicketLogsDetails}/${id}`,
      }),
    }),

    getTicketLogActivity: builder.query({
      query: (id) => ({
        url: `${API_URL.getTicketLogActivity}/${id}`,
      }),
    }),

    addCc: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addCc}?employeeId=${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { ticket_log_id }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetTicketIDRelatedNotifications",
        { type: "GetTicketDetails", id: ticket_log_id },
        { type: "GetCCList", id: ticket_log_id },
      ],
    }),

    getEmployeeListToAddCC: builder.query<
      GetEmployeeListToAddCCResponseType,
      GetQueryArgsType<GetEmployeeListToAddCCParamsType>
    >({
      query: ({ params }) => ({
        url: `${API_URL.getEmployeeListToAddCC}/${params?.id}`,
        params: {
          search: params?.search,
          dept_ids: params?.dept_ids,
        },
      }),
      providesTags: (_, __, { params }) => [
        { type: "GetCCList", id: params?.id },
      ],
    }),

    cancelTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.cancelTicket}/${id}/cancel`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTickets",
        "GetAllMyTicketDetails",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    getTicketIDRelatedNotifications: builder.query<
      GetTicketIDRelatedNotificationsResponseType,
      GetQueryArgsType<GetTicketIDRelatedNotificationsParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getTicketIDRelatedNotifications,
        ...queryArgs,
      }),
      providesTags: ["GetTicketIDRelatedNotifications"],
    }),

    getTicketForCC: builder.query<
      GetTicketForCCResponseType,
      GetQueryArgsType<GetTicketForCCParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getTicketForCC,
        ...queryArgs,
      }),
      providesTags: ["GetTicketForCC"],
    }),

    // comments
    addComment: builder.mutation({
      query: (data) => ({
        url: API_URL.addComment,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { ticketLogId }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetCommentHistory",
        { type: "GetCommentHistory", id: ticketLogId },
        { type: "GetTicketDetails", id: ticketLogId },
        { type: "GetCCList", id: ticketLogId },
      ],
    }),
    editComment: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.editComment}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { data }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetCommentHistory",
        { type: "GetCommentHistory", id: data?.ticketLogId },
        { type: "GetTicketDetails", id: data?.ticketLogId },
        { type: "GetCCList", id: data?.ticketLogId },
      ],
    }),

    deleteComment: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.deleteComment}/${id}/user/${data?.userId}`,
        method: "POST",
      }),
      invalidatesTags: (_, __, { data }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetCommentHistory",
        { type: "GetCommentHistory", id: data?.ticketLogId },
        { type: "GetTicketDetails", id: data?.ticketLogId },
        { type: "GetCCList", id: data?.ticketLogId },
      ],
    }),

    updateTicketStatus: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateTicketStatus}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetAllTickets"],
    }),

    closeTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.closeTicket}/${id}/status/completed`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTickets",
        "GetAllMyTicketDetails",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    getOneToOneChatList: builder.query<
      ChatListResponse,
      GetQueryArgsType<ChatListParamsType>
    >({
      query: ({ params }) => ({
        url: `${API_URL.getOneToOneChatList}/${params?.id}`,
        params: {
          search: params?.search,
          type: params?.type,
        },
      }),

      providesTags: ["GetOneToOneChatList"],
    }),

    getOneToOneConversation: builder.query<ConversationResponse, any>({
      query: ({ id, chatId, params }) => ({
        url: `${API_URL.getOneToOneConversation}/${chatId}/${id}`,
        params,
      }),
      providesTags: ["GetOneToOneConversation"],
    }),
    getGroupConversation: builder.query<ConversationResponse, any>({
      query: ({ chatId, params }) => ({
        url: `${API_URL.getGroupConversation}/${chatId}`,
        params,
      }),
      providesTags: (_, __, { chatId }) => [
        {
          type: "GetGroupChat",
          // id: chatId,
        },
      ],
    }),

    getMailList: builder.query<
      GetMailListResponse,
      GetQueryArgsType<GetMailListParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getMailList,
        ...queryArgs,
      }),
      providesTags: ["GetMailList"],
    }),

    getSingleMailById: builder.query<SingleMailResponse, any>({
      query: (id) => ({
        url: `${API_URL.getSingleMailById}/${id}`,
      }),
      providesTags: ["GetSingleMailById"],
    }),

    sendMail: builder.mutation({
      query: (data) => ({
        url: API_URL.sendMail,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetMailList"],
    }),

    getUserByEmail: builder.query({
      query: (email) => ({
        url: `${API_URL.getUserByEmail}?email=${email}`,
      }),
    }),

    replyMail: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.replyMail}?id=${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetReplyMailByParentMail", "GetSingleMailById"],
    }),

    getReplyMailByParentMail: builder.query<
      GetReplyMailByParentMailResponse,
      any
    >({
      query: (id) => ({
        url: `${API_URL.getReplyMailByParentMail}/${id}`,
      }),
      providesTags: ["GetReplyMailByParentMail"],
    }),

    starredMail: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.starredMail}/${id}`,
        method: "POST",
        body: data,
      }),

      invalidatesTags: ["GetMailList", "GetSingleMailById"],
    }),

    deleteMail: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.deleteMail}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetMailList"],
    }),

    deleteMultipleMail: builder.mutation({
      query: ({ data }) => ({
        url: `${API_URL.deleteMultipleMail}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetMailList"],
    }),

    addSaveDraftMail: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addSaveDraftMail}?id=${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetMailList"],
    }),

    updateDraftMail: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateSaveDraftMail}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["GetMailList"],
    }),

    getChatUserList: builder.query<
      GetChatUserListResponse,
      GetQueryArgsType<GetChatUserListParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getChatUserList,
        ...queryArgs,
      }),
      // providesTags: ["GetChatUserList"],
    }),

    createGroupChat: builder.mutation({
      query: (data) => ({
        url: API_URL.createGroupChat,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
      ],
    }),
    getGroupChatDetails: builder.query<GetGroupChatDetailsResponse, any>({
      query: (id) => ({
        url: `${API_URL.getGroupChatDetails}/${id}`,
      }),
      providesTags: ["GetGroupChatDetails"],
    }),
    addGroupPhoto: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addGroupPhoto}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
      ],
    }),
    addGroupName: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addGroupName}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
      ],
    }),
    addGroupMember: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addGroupMember}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
      ],
    }),
    getGroupMember: builder.query<GetGroupMemberResponse, any>({
      query: (id) => ({
        url: `${API_URL.getGroupMember}/${id}`,
      }),
      providesTags: ["GetGroupMember"],
    }),
    leaveFromGroup: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.leaveFromGroup}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetOneToOneChatList"],
    }),
    getGroupAttachments: builder.query<GetGroupAttachmentsResponse, any>({
      query: (id) => ({
        url: `${API_URL.getGroupAttachments}/${id}`,
      }),
      providesTags: ["GetGroupAttachments"],
    }),
    getChatAttachments: builder.query<GetGroupAttachmentsResponse, any>({
      query: ({ id, params }) => ({
        url: `${API_URL.getChatAttachments}/${id}`,
        params,
      }),
      providesTags: ["GetChatAttachments"],
    }),

    updateRead: builder.mutation({
      query: ({ id, userId }) => ({
        url: `${API_URL.updateRead}/${id}?userId=${userId}`,
        method: "PATCH",
        body: {},
      }),
    }),

    updateGroupName: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateGroupName}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    updateGroupPhoto: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateGroupPhoto}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    getFilterGroupUser: builder.query<GetFilterGroupResponse, any>({
      query: ({ id, params }) => ({
        url: `${API_URL.getFilterGroupUser}/${id}`,
        params,
      }),
      providesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    updateGroupMember: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateGroupMember}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    deleteSpecificMessage: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.deleteSpecificMessage}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    deleteAllChat: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.deleteAllChat}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetFilterGroupUser",
        "GetOneToOneChatList",
        "GetGroupChat",
        "GetGroupChatDetails",
        "GetGroupMember",
        "GetOneToOneConversation",
      ],
    }),

    getTags: builder.query<GetTagsResponse, void>({
      query: () => ({
        url: `${API_URL.getTags}`,
      }),
      providesTags: ["GetTags"],
    }),

    getCommentHistory: builder.query<GetCommentHistoryResponse, any>({
      query: (id) => ({
        url: `${API_URL.getCommentHistory}/${id}`,
      }),
      providesTags: ["GetCommentHistory"],
    }),

    updateForwardTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateForwardTicket}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    forwardOpenTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.forwardOpenTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetAllMyTicketDetails", "GetAllMyTickets"],
    }),
    resolveOpenTicket: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.resolveOpenTicket}/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["GetAllMyTicketDetails", "GetAllMyTickets"],
    }),

    getNotifications: builder.query<GetNotifications, any>({
      query: (id) => ({
        url: `${API_URL.notification}?receiverId=${id}&check=false`,
      }),
      providesTags: ["GetNotifications"],
    }),
    getNotificationCount: builder.query<GetNotificationCount, any>({
      query: (id) => ({
        url: `${API_URL.notificationCount}?receiverId=${id}&check=false`,
      }),
      providesTags: [{ type: "GetNotifications", id: "count" }],
    }),
    checkNotification: builder.mutation({
      query: ({ id, receiverId }) => ({
        url: `${API_URL.checkNotification}/${id}/check?receiverId=${receiverId}`,
        method: "PATCH",
      }),
      invalidatesTags: [
        "GetNotifications",
        { type: "GetNotifications", id: "count" },
      ],
    }),
    checkNotifications: builder.mutation({
      query: ({ ids, receiverId }) => ({
        url: `${API_URL.checkNotifications}?receiverId=${receiverId}`,
        body: {
          notificationIds: ids,
        },
        method: "PATCH",
      }),
      invalidatesTags: [
        "GetNotifications",
        { type: "GetNotifications", id: "count" },
      ],
    }),
  }),
});

export const {
  useCreateTicketMutation,
  useCheckKeyAvailabilityMutation,
  useUpdateTicketMutation,
  useRaiseTicketMutation,
  useUpdateRaisedTicketMutation,
  useReopenTicketMutation,
  useGetAllTicketsQuery,
  useGetTicketDetailsQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useGetAllCategoriesQuery,
  useUploadPhotoMutation,
  useMyRaisedTicketsQuery,
  useGetAllTicketLogsQuery,
  useDeleteTicketMutation,
  useGetTicketLogDetailsQuery,
  useGetApprovalHubTicketsQuery,
  useApproveRaiseTicketMutation,
  useRejectRaiseTicketMutation,
  useShiftPriorityMutation,
  useGetTicketLogsDetailsQuery,
  useGetTicketLogActivityQuery,
  useAddCcMutation,
  useGetEmployeeListToAddCCQuery,
  useCancelTicketMutation,
  useCloseTicketMutation,
  useGetTicketIDRelatedNotificationsQuery,
  useGetTicketForCCQuery,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useEditCommentMutation,
  useUpdateTicketStatusMutation,
  useGetOneToOneChatListQuery,
  useGetOneToOneConversationQuery,
  useGetMailListQuery,
  useGetSingleMailByIdQuery,
  useSendMailMutation,
  useLazyGetUserByEmailQuery,
  useGetUserByEmailQuery,
  useReplyMailMutation,
  useStarredMailMutation,
  useDeleteMailMutation,
  useDeleteMultipleMailMutation,
  useAddSaveDraftMailMutation,
  useGetChatUserListQuery,
  useCreateGroupChatMutation,
  useGetReplyMailByParentMailQuery,
  useGetGroupConversationQuery,
  useGetGroupChatDetailsQuery,
  useAddGroupMemberMutation,
  useAddGroupNameMutation,
  useAddGroupPhotoMutation,
  useGetGroupAttachmentsQuery,
  useGetGroupMemberQuery,
  useLeaveFromGroupMutation,
  useGetChatAttachmentsQuery,
  useUpdateReadMutation,
  useUpdateGroupNameMutation,
  useUpdateGroupPhotoMutation,
  useGetFilterGroupUserQuery,
  useUpdateGroupMemberMutation,
  useDeleteSpecificMessageMutation,
  useDeleteAllChatMutation,
  useGetTagsQuery,
  useGetCommentHistoryQuery,
  useUpdateForwardTicketMutation,
  useForwardOpenTicketMutation,
  useResolveOpenTicketMutation,
  useUpdateDraftMailMutation,
  useGetNotificationsQuery,
  useGetNotificationCountQuery,
  useCheckNotificationMutation,
  useCheckNotificationsMutation,
} = ticketApi;
